// src/components/pages/Hero.js
import React, { useEffect, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import reviewsData from "../../data/reviews.json"; // adjust the path as needed
import Gallery from "./Gallery";

// Helper component to render star SVGs for the rating
function StarRating({ rating }) {
  const numStars = parseInt(rating, 10);
  return (
    <div className="star-rating">
      {Array.from({ length: numStars }).map((_, index) => (
        <svg key={index} width="20" height="20" viewBox="0 0 20 20">
          <path
            fill="rgba(251,67,60,1)"
            opacity="1"
            d="M0 4C0 1.79086 1.79086 0 4 0H10V20H4C1.79086 20 0 18.2091 0 16V4Z"
          ></path>
          <path
            fill="rgba(251,67,60,1)"
            opacity="1"
            d="M20 4C20 1.79086 18.2091 0 16 0H10V20H16C18.2091 20 20 18.2091 20 16V4Z"
          ></path>
          <path
            fill="rgba(255,255,255,1)"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 13.3736L12.5949 14.7111C12.7378 14.7848 12.9006 14.8106 13.0593 14.7847C13.4681 14.718 13.7454 14.3325 13.6787 13.9237L13.2085 11.0425L15.2824 8.98796C15.3967 8.8748 15.4715 8.72792 15.4959 8.569C15.5588 8.15958 15.2779 7.77672 14.8685 7.71384L11.983 7.2707L10.6699 4.66338C10.5975 4.51978 10.481 4.40322 10.3374 4.33089C9.96742 4.14458 9.51648 4.29344 9.33017 4.66338L8.01705 7.2707L5.13157 7.71384C4.97265 7.73825 4.82577 7.81309 4.71261 7.92731C4.42109 8.22158 4.42332 8.69645 4.71759 8.98796L6.79152 11.0425L6.32131 13.9237C6.29541 14.0824 6.3212 14.2452 6.39486 14.3881C6.58464 14.7563 7.03696 14.9009 7.40514 14.7111L10 13.3736Z"
          ></path>
        </svg>
      ))}
    </div>
  );
}

function Hero() {
  const logoContainerRef = useRef(null);

  // Trigger sheen animation once on initial page load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (logoContainerRef.current) {
        logoContainerRef.current.classList.add('sheen-animate');
        // Remove class after animation so hover can re-trigger
        setTimeout(() => {
          if (logoContainerRef.current) {
            logoContainerRef.current.classList.remove('sheen-animate');
          }
        }, 800);
      }
    }, 500); // Small delay after page load for better effect
    return () => clearTimeout(timer);
  }, []);

  // Slider settings: explicitly show one slide at a time and disable center mode
  const sliderSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1, // one slide at a time
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    centerMode: false,
    adaptiveHeight: true,
  };

  return (
    <main>
      <section className="hero" aria-label="Welcome to Clipper Kings Barbershop">
        <video autoPlay muted loop playsInline className="hero-video" aria-hidden="true">
          <source src="/videos/IMG_7931.mp4" type="video/mp4" />
        </video>
        <header className="hero-content">
          <div className="hero-logo-container" ref={logoContainerRef}>
            <img
              src="/images/logo.png"
              alt="Clipper Kings Ornate Logo"
              className="hero-logo"
            />
          </div>
          <a
            href="https://getsquire.com/booking/book/evergreen-barbershop-san-jose"
            target="_blank"
            rel="noopener noreferrer"
            className="hero-book-btn"
          >
            Book Now
          </a>
          <p className="tagline" style={{ fontSize: "1.2em" }}>
            Dedicated to our craft. Come experience real <em>quality</em> service & skill.
          </p>
        </header>
      </section>
      <section className="testimonials-section" id="testimonials-section" aria-label="Customer Testimonials">
        <div className="testimonials-overlay"></div>
        <h2>Testimonials</h2>
        <Slider {...sliderSettings}>
          {reviewsData.users.map((user, index) => (
            <div key={index} className="testimonial-slide">
              <div className="testimonial">
                <img
                  src={user.Image}
                  alt={user.Username}
                  className="testimonial-image"
                />
                <p className="review-text">"{user.Review}"</p>
                <p className="review-author">- {user.Username}</p>
                <StarRating rating={user.Stars} />
                <a
                  className="review-userlink"
                  href={user.Userlink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Yelp
                </a>
              </div>
            </div>
          ))}
        </Slider>
      </section>
      <Gallery />
    </main>
  );
}

export default Hero;
